import { getLocaleDateString } from 'app/components/helpers'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import React, { MouseEvent, useState, useContext } from 'react'
import { getVerdictLabel } from '../../helpers'
import { SignalVerdict } from '../../types'
import { applicationConfiguration } from 'app/modules/shared'

const RemoveButton = ({
  nonRemovable,
  handleRemove,
  id,
}: {
  nonRemovable: boolean
  handleRemove: (e?: MouseEvent<HTMLElement>) => void
  id: string
}) => {
  if (nonRemovable) {
    return <span></span>
  }
  return (
    <span className="remove-filter-btn" onClick={handleRemove} id={id} role="button">
      <i className="ds3-icon ds3-close" />
    </span>
  )
}

const FilterChip = ({
  filters,
  filterName,
  filterValue,
  onRemoveFilter,
  allowShort,
  shortLimit = 3,
  nonRemovable = false,
}: {
  filters: { [key: string]: any }
  filterName: string
  filterValue: string
  onRemoveFilter: (this: any, filterName: string, filterValue: string | null) => any
  allowShort?: boolean
  shortLimit?: number
  nonRemovable?: boolean
}) => {
  const { _t } = useTypedTranslation()
  const [full, setFull] = useState(false)
  const { useHumanFriendlyVerdicts } = useContext(applicationConfiguration)

  const hide =
    ['method', 'unique_files', 'rate_to', 'date_to', 'derived_files'].includes(filterName) ||
    (filterName === 'date_from' && filterValue === '' && filters?.date_to === '')

  if (hide) {
    return null
  }

  const handleRemove = (e?: MouseEvent<HTMLElement>) => {
    const value = e?.currentTarget?.id
    onRemoveFilter(filterName, value ?? null)
  }

  const getChipContent = () => {
    // Comma separated lists
    if (
      filterName === 'tag' ||
      filterName === 'verdict' ||
      filterName === 'yara_rule' ||
      filterName === 'signal_groups' ||
      filterName === 'mitre_techniques'
    ) {
      const value = filterValue.split(',')
      const shortened = !allowShort || full || value.length <= shortLimit ? value : value.slice(0, shortLimit)

      return filterValue.length ? (
        <>
          <span className="filter-name text-note text-m">{_t(filterName.replace('_', '-'))}: </span>
          <div className="filter-values">
            {shortened.map((val: string) => (
              <div key={val} className="filter-value-container">
                <span className="filter-value text-note ellipsis">
                  {filterName === 'verdict' ? getVerdictLabel(val as SignalVerdict, useHumanFriendlyVerdicts) : _t(val)}
                </span>
                <RemoveButton nonRemovable={nonRemovable} handleRemove={handleRemove} id={val} />
              </div>
            ))}
            {shortened.length < value.length && !full ? (
              <div className="filter-value-container">
                <span className="remove-filter-btn" onClick={() => setFull(true)} role="button">
                  +{value.length - shortLimit}
                </span>
              </div>
            ) : null}
          </div>
        </>
      ) : null
    }

    if (filterName === 'rate_from') {
      return (
        <>
          <span className="filter-name text-note text-m">{_t('detection-rate')}: </span>
          <div className="filter-value-container">
            <span className="filter-value text-note ellipsis">
              {filterValue}% - {filters?.rate_to || '100'}%
            </span>
            <RemoveButton nonRemovable={nonRemovable} handleRemove={handleRemove} id={filterValue} />
          </div>
        </>
      )
    }

    if (filterName === 'date_from') {
      return (
        <>
          <span className="filter-name text-note text-m">{_t('date')}: </span>
          <div className="filter-value-container">
            <span className="filter-value text-note ellipsis">
              {`${getLocaleDateString(new Date(filterValue))}
                ${
                  new Date(filterValue).setHours(0, 0, 0, 0) === new Date(filters?.date_to).setHours(0, 0, 0, 0)
                    ? ''
                    : ` - ${getLocaleDateString(new Date(filters?.date_to))}`
                }
              `}
            </span>
            <RemoveButton nonRemovable={nonRemovable} handleRemove={handleRemove} id={filterValue} />
          </div>
        </>
      )
    }

    const filterNameLabel = filterName === 'query' ? _t('search') : _t(filterName.replace(/_/g, '-'))

    return (
      <>
        <span className="filter-name text-note text-m">{filterNameLabel}: </span>
        <div className="filter-value-container">
          <span className="filter-value text-note ellipsis">{_t(filterValue)}</span>
          <RemoveButton nonRemovable={nonRemovable} handleRemove={handleRemove} id={filterValue} />
        </div>
      </>
    )
  }

  const content = getChipContent()

  return <div className={`filter-chip${!content ? ' d-none' : ''}`}>{content}</div>
}

const FilterSummary = ({
  filters,
  onRemoveFilter,
  allowShort,
  nonRemovable,
}: {
  filters: { [key: string]: any }
  onRemoveFilter: (this: any, filterName: string, filterValue: string | null) => any
  allowShort?: boolean
  nonRemovable?: boolean
}) => {
  return Object.entries(filters).map(([filterName, filterValue]) =>
    filterValue ? (
      <FilterChip
        key={filterName}
        filters={filters}
        filterName={filterName}
        filterValue={filterValue}
        onRemoveFilter={onRemoveFilter}
        allowShort={allowShort}
        nonRemovable={nonRemovable}
      />
    ) : null
  )
}

export default FilterSummary
